import { useState, useRef, useCallback, useEffect } from 'react';
import { useWindowSize } from './useWindowSize';
import { useLazyGetUserHoverCardQuery } from 'store/services/profile/profileApi';
import { UserHoverCardData } from 'types/interfaces/hoverCard';

const SHOW_DELAY = 300;
const HIDE_GRACE = 150;
// The trader card (Asana 1219748562973072): 312 wide, about 470 tall for
// Standard and about 540 for Elite with 8 badges; the estimate takes the taller.
const CARD_WIDTH = 312;
const CARD_HEIGHT_ESTIMATE = 540;
const GAP = 8;
const EDGE_PADDING = 16;

export interface HoverCardPosition {
  left: number;
  top: number;
}

interface UseHoverCardReturn {
  triggerProps: {
    onMouseEnter: (e: React.MouseEvent) => void;
    onMouseLeave: () => void;
  };
  cardProps: {
    onMouseEnter: () => void;
    onMouseLeave: () => void;
  };
  isVisible: boolean;
  position: HoverCardPosition;
  data: UserHoverCardData | undefined;
  isLoading: boolean;
}

function calculatePosition(triggerRect: DOMRect): HoverCardPosition {
  const { innerWidth, innerHeight } = window;

  let left = triggerRect.left;
  let top = triggerRect.bottom + GAP;

  // Flip above if too close to bottom
  if (top + CARD_HEIGHT_ESTIMATE > innerHeight) {
    top = triggerRect.top - CARD_HEIGHT_ESTIMATE - GAP;
  }

  // A tall card on a short viewport: never off the top either
  if (top < EDGE_PADDING) {
    top = EDGE_PADDING;
  }

  // Shift left if too close to right edge
  if (left + CARD_WIDTH > innerWidth - EDGE_PADDING) {
    left = innerWidth - CARD_WIDTH - EDGE_PADDING;
  }

  // Don't go off-screen left
  if (left < EDGE_PADDING) {
    left = EDGE_PADDING;
  }

  return { left, top };
}

export const useHoverCard = (username: string, enabled: boolean = true): UseHoverCardReturn => {
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState<HoverCardPosition>({ left: 0, top: 0 });
  const showTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { isMobile } = useWindowSize();
  const [trigger, { data, isLoading, isError }] = useLazyGetUserHoverCardQuery();

  const clearTimers = useCallback(() => {
    if (showTimerRef.current) {
      clearTimeout(showTimerRef.current);
      showTimerRef.current = null;
    }
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
  }, []);

  // Clean up timers on unmount
  useEffect(() => clearTimers, [clearTimers]);

  // Hide on error
  useEffect(() => {
    if (isError) setIsVisible(false);
  }, [isError]);

  const startShow = useCallback(
    (e: React.MouseEvent) => {
      if (!enabled || isMobile) return;

      // Clear any pending hide
      if (hideTimerRef.current) {
        clearTimeout(hideTimerRef.current);
        hideTimerRef.current = null;
      }

      const triggerEl = e.currentTarget as HTMLElement;

      showTimerRef.current = setTimeout(() => {
        const rect = triggerEl.getBoundingClientRect();
        setPosition(calculatePosition(rect));
        trigger(username, true); // true = preferCacheValue
        setIsVisible(true);
        showTimerRef.current = null;
      }, SHOW_DELAY);
    },
    [enabled, isMobile, trigger, username]
  );

  const startHide = useCallback(() => {
    // Clear any pending show
    if (showTimerRef.current) {
      clearTimeout(showTimerRef.current);
      showTimerRef.current = null;
    }

    hideTimerRef.current = setTimeout(() => {
      setIsVisible(false);
      hideTimerRef.current = null;
    }, HIDE_GRACE);
  }, []);

  const cancelHide = useCallback(() => {
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
  }, []);

  const triggerProps = {
    onMouseEnter: startShow,
    onMouseLeave: startHide,
  };

  const cardProps = {
    onMouseEnter: cancelHide,
    onMouseLeave: startHide,
  };

  return {
    triggerProps,
    cardProps,
    isVisible: enabled && !isMobile && isVisible,
    position,
    data,
    isLoading,
  };
};
